import { ActionKeys } from '~/actions/constants';
import { ActionTypes } from '~/actions/index';
import { NotificationType } from '~/types/EmailNotifications';

export interface NotificationsState {
  readonly isLoadingNotifications: boolean;
  readonly isAutoOrderEmailEnabled: boolean;
  readonly isAutoDeliveredEmailEnabled: boolean;
  readonly sendingSampleShippedEmail: boolean;
  readonly sampleShippedEmailSent: boolean;
  readonly sampleShippedEmailFailed: boolean;
  readonly sendingSampleDeliveredEmail: boolean;
  readonly sampleDeliveredEmailSent: boolean;
  readonly sampleDeliveredEmailFailed: boolean;
  readonly autoOrderEmailDelayMinutes: Maybe<number>;
  readonly autoOrderEmailCustomText: Maybe<string>;
  readonly autoDeliveredEmailCustomText: Maybe<string>;
  readonly orderShippedEmailSettingSubmitSuccess: boolean;
  readonly orderShippedEmailSettingSubmitFailed: boolean;
  readonly orderArrivedEmailSettingSubmitSuccess: boolean;
  readonly orderArrivedEmailSettingSubmitFailed: boolean;
}

export const initialState: NotificationsState = {
  isLoadingNotifications: false,
  isAutoOrderEmailEnabled: false,
  isAutoDeliveredEmailEnabled: false,
  sendingSampleShippedEmail: false,
  sampleShippedEmailSent: false,
  sampleShippedEmailFailed: false,
  sendingSampleDeliveredEmail: false,
  sampleDeliveredEmailSent: false,
  sampleDeliveredEmailFailed: false,
  autoOrderEmailDelayMinutes: null,
  autoOrderEmailCustomText: null,
  autoDeliveredEmailCustomText: null,
  orderShippedEmailSettingSubmitSuccess: false,
  orderShippedEmailSettingSubmitFailed: false,
  orderArrivedEmailSettingSubmitSuccess: false,
  orderArrivedEmailSettingSubmitFailed: false,
};

function notificationsReducer(state: NotificationsState = initialState, action: ActionTypes) {
  switch (action.type) {
    case ActionKeys.FETCH_NOTIFICATIONS:
      return {
        ...state,
        isLoadingNotifications: true,
      };
    case ActionKeys.FETCH_NOTIFICATIONS_SUCCESS:
      return {
        ...state,
        isLoadingNotifications: false,
        isAutoOrderEmailEnabled: action.payload.autoEmailNotifications,
        isAutoDeliveredEmailEnabled: action.payload.autoDeliveredEmailNotifications,
        autoOrderEmailDelayMinutes: action.payload.autoOrderEmailDelayMinutes,
        autoOrderEmailCustomText: action.payload.autoOrderEmailCustomText,
        autoDeliveredEmailCustomText: action.payload.autoDeliveredEmailCustomText,
      };
    case ActionKeys.UPDATE_ORDER_SHIPPED_EMAIL_SETTINGS:
      return {
        ...state,
        orderShippedEmailSettingSubmitSuccess: false,
        orderShippedEmailSettingSubmitFailed: false,
      };
    case ActionKeys.UPDATE_ORDER_SHIPPED_EMAIL_SETTINGS_ERROR:
      return {
        ...state,
        orderShippedEmailSettingSubmitFailed: true,
      };
    case ActionKeys.UPDATE_ORDER_SHIPPED_EMAIL_SETTINGS_SUCCESS:
      return {
        ...state,
        orderShippedEmailSettingSubmitSuccess: true,
        isAutoOrderEmailEnabled: action.payload.orderShippedFormFields.orderShippingEmailEnabled,
        autoOrderEmailDelayMinutes:
          action.payload.orderShippedFormFields.orderShippingEmailDelaySendDuration,
        autoOrderEmailCustomText: action.payload.orderShippedFormFields.orderShippingEmailContent,
      };
    case ActionKeys.UPDATE_ORDER_ARRIVED_EMAIL_SETTINGS:
      return {
        ...state,
        orderArrivedEmailSettingSubmitSuccess: false,
        orderArrivedEmailSettingSubmitFailed: false,
      };
    case ActionKeys.UPDATE_ORDER_ARRIVED_EMAIL_SETTINGS_ERROR:
      return {
        ...state,
        orderArrivedEmailSettingSubmitFailed: true,
      };
    case ActionKeys.UPDATE_ORDER_ARRIVED_EMAIL_SETTINGS_SUCCESS:
      return {
        ...state,
        orderArrivedEmailSettingSubmitSuccess: true,
        isAutoDeliveredEmailEnabled: action.payload.orderArrivedFormFields.orderArrivedEmailEnabled,
        autoDeliveredEmailCustomText:
          action.payload.orderArrivedFormFields.orderArrivedEmailContent,
      };
    case ActionKeys.SEND_SAMPLE_EMAIL:
      if (action.notificationType === NotificationType.Shipped) {
        return {
          ...state,
          sendingSampleShippedEmail: true,
          sampleShippedEmailSent: false,
          sampleShippedEmailFailed: false,
        };
      }
      return {
        ...state,
        sendingSampleDeliveredEmail: true,
        sampleDeliveredEmailSent: false,
        sampleDeliveredEmailFailed: false,
      };
    case ActionKeys.SEND_SAMPLE_EMAIL_SUCCESS:
      if (action.notificationType === NotificationType.Shipped) {
        return {
          ...state,
          sendingSampleShippedEmail: false,
          sampleShippedEmailSent: true,
          sampleShippedEmailFailed: false,
        };
      }
      return {
        ...state,
        sendingSampleDeliveredEmail: false,
        sampleDeliveredEmailSent: true,
        sampleDeliveredEmailFailed: false,
      };
    case ActionKeys.SEND_SAMPLE_EMAIL_ERROR:
      if (action.notificationType === NotificationType.Shipped) {
        return {
          ...state,
          sendingSampleShippedEmail: false,
          sampleShippedEmailSent: false,
          sampleShippedEmailFailed: true,
        };
      }
      return {
        ...state,
        sendingSampleDeliveredEmail: false,
        sampleDeliveredEmailSent: false,
        sampleDeliveredEmailFailed: true,
      };
    case ActionKeys.CLEAR_SAMPLE_EMAIL_ERRORS:
      return {
        ...state,
        sendingSampleShippedEmail: false,
        sampleShippedEmailSent: false,
        sampleShippedEmailFailed: false,
        sendingSampleDeliveredEmail: false,
        sampleDeliveredEmailSent: false,
        sampleDeliveredEmailFailed: false,
      };
    default:
      return state;
  }
}

export default notificationsReducer;
